import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { AstrolabeGlyph, PlanetDot, DustMotes } from "./motifs";

const API = process.env.REACT_APP_BACKEND_URL;

const SUGGESTED = [
  "Tell me about her Salesforce experience.",
  "Does she have aerospace industry experience?",
  "What's her approach to supporting a busy CEO?",
  "How does she handle scattered operations?",
];

const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

const OPENER = {
  id: "opener",
  role: "assistant",
  content:
    "Hi! I'm Barbara's AI assistant, grounded in her real experience. Ask me anything about her background, skills, or how she'd support your team — or tap a suggestion below.",
};

export default function RecruiterChat() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([OPENER]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const taRef = useRef(null);
  const sessionIdRef = useRef(
    (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now())
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [msgs, busy]);

  useEffect(() => {
    if (open) setTimeout(() => taRef.current?.focus(), 250);
  }, [open]);

  async function send(text) {
    const clean = (text || input).trim();
    if (!clean || busy) return;
    const nextHistory = [...msgs, { id: uid(), role: "user", content: clean }];
    setMsgs(nextHistory);
    setInput("");
    setBusy(true);
    try {
      // Exclude the OPENER and any client-side error turns from what we send to the model.
      const cleanHistory = msgs
        .filter((m) => m !== OPENER && !m.error)
        .map((m) => ({ role: m.role, content: m.content }));
      const res = await fetch(`${API}/api/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Session-Id": sessionIdRef.current },
        body: JSON.stringify({ message: clean, history: cleanHistory }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong.");
      setMsgs([...nextHistory, { id: uid(), role: "assistant", content: data.answer }]);
    } catch (e) {
      setMsgs([
        ...nextHistory,
        { id: uid(), role: "assistant", content: `I couldn't reach the model right now. Please try again in a moment.`, error: true },
      ]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/* FAB */}
      <button
        aria-label="Chat with Barbara's AI assistant"
        data-testid="recruiter-chat-fab"
        data-cosmic="true"
        onClick={() => setOpen(true)}
        className={`fixed z-[55] bottom-5 right-5 sm:bottom-6 sm:right-6 grid place-items-center w-14 h-14 rounded-full bg-warmdark text-honey border border-brass/50 shadow-[0_18px_50px_-10px_rgba(74,51,34,0.55)] hover:bg-caramel transition-colors warm-glow ${open ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      >
        <AstrolabeGlyph size={22} color="#E0B84C" />
        <span className="sr-only">Open recruiter Q&amp;A chat</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-[56] bottom-4 right-4 sm:bottom-6 sm:right-6 w-[min(24rem,calc(100vw-2rem))] h-[min(560px,calc(100vh-2rem))] rounded-[1.4rem] overflow-hidden bg-ivory border border-brass/30 shadow-[0_30px_80px_rgba(35,31,28,0.35)] observatory-frame flex flex-col"
            data-testid="recruiter-chat-panel"
            role="dialog"
            aria-label="Recruiter Q&A chat"
          >
            {/* header */}
            <div className="relative bg-warmdark text-ivory px-5 py-4 flex items-center gap-3 overflow-hidden">
              <DustMotes count={5} />
              <span className="grid place-items-center w-9 h-9 rounded-full bg-honey/15 border border-honey/30">
                <AstrolabeGlyph size={16} color="#E0B84C" />
              </span>
              <div className="min-w-0">
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-honey/80 flex items-center gap-2">
                  <PlanetDot /> Recruiter · Q&amp;A
                </div>
                <div className="font-display text-lg leading-tight truncate">Ask about Barbara</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="recruiter-chat-close"
                className="ml-auto grid place-items-center w-8 h-8 rounded-full hover:bg-ivory/10 transition-colors text-honey"
                aria-label="Close chat"
              >
                <X size={16} />
              </button>
            </div>

            {/* messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-parchment"
              data-testid="recruiter-chat-messages"
            >
              {msgs.map((m) => (
                <div
                  key={m.id}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                      m.role === "user"
                        ? "bg-terracotta text-ivory rounded-tr-md"
                        : "bg-ivory text-caramel border border-brass/25 rounded-tl-md"
                    }`}
                  >
                    {m.role === "assistant"
                      ? m.content.replace(/\*\*(.+?)\*\*/g, "$1").replace(/\*(.+?)\*/g, "$1")
                      : m.content}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="bg-ivory text-caramel border border-brass/25 rounded-2xl rounded-tl-md px-4 py-3 text-sm inline-flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse [animation-delay:120ms]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse [animation-delay:240ms]" />
                  </div>
                </div>
              )}

              {msgs.length <= 1 && !busy && (
                <div className="pt-2">
                  <div className="font-mono-tech text-[10px] tracking-widest uppercase text-warmmuted mb-2">
                    Try
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTED.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        data-testid="recruiter-chat-suggested"
                        className="font-mono-tech text-[11px] tracking-wide px-3 py-1.5 rounded-full border border-brass/40 text-caramel hover:bg-terracotta hover:text-ivory hover:border-terracotta transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* composer */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-brass/25 bg-ivory p-3 flex items-end gap-2"
            >
              <textarea
                ref={taRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Ask a question…"
                rows={1}
                maxLength={800}
                data-testid="recruiter-chat-input"
                className="flex-1 resize-none rounded-2xl border border-brass/25 bg-parchment px-4 py-2.5 text-sm text-caramel placeholder:text-warmmuted/70 outline-none focus:border-terracotta max-h-24"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="recruiter-chat-send"
                data-cosmic="true"
                className="grid place-items-center w-10 h-10 rounded-full bg-terracotta text-ivory disabled:opacity-40 hover:bg-warmdark transition-colors shrink-0"
                aria-label="Send message"
              >
                <Send size={16} />
              </button>
            </form>
            <div className="px-4 pb-3 -mt-2 font-mono-tech text-[9px] tracking-widest uppercase text-warmmuted flex items-center gap-1.5">
              <Sparkles size={10} /> Grounded in Barbara's real experience · Claude Sonnet 4.6
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
